import {
  IMPORT_FIELDS,
  IMPORT_FIELD_LABELS,
  IMPORT_STATUS_LABELS,
  formatDateTime,
  importListSchema,
  importMappingSchema,
  importSchema,
  importTemplateSchema,
  mappingFromTemplate,
  type Import,
  type ImportFieldName,
  type ImportTemplate,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, FolderKanban, Save, Trash2, Upload } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormAlert, FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { saveBlobFile } from '@/lib/platform';

const templateListSchema = z.object({ items: z.array(importTemplateSchema) });
const OPTIONAL: ImportFieldName[] = ['unit', 'value', 'target', 'reason'];
const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger';

/**
 * Indicadores › Importações (T-200c, RF-45, RN-18, RN-19; DEC-066): enviar a planilha, mapear as colunas (ou aplicar
 * um modelo), validar com as inconsistências por linha, confirmar, acompanhar o processamento e baixar o log.
 */
export function ImportacoesPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Importações"
        description="Resultados e metas a partir de planilhas .xlsx ou .csv: mapeie as colunas, confira as inconsistências de cada linha e confirme. A origem fica registrada como importada."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="A importação grava resultados e metas de um projeto. Quando a empresa tiver um projeto em andamento, ela aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <ImportSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function ImportSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const can = useCan();
  const canImport = !readOnly && can('importacoes', 'create');
  // Confirmar grava resultados e metas: a mesma permissão do lançamento e da grade de metas (DEC-066).
  const canRecord = can('metas-resultados', 'edit');
  const userId = useCurrentUser().id;
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/imports`;
  const [params, setParams] = useSearchParams();
  const openId = params.get('importacao');
  const open = (id: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (id) next.set('importacao', id);
        else next.delete('importacao');
        return next;
      },
      { replace: true },
    );
  const list = useQuery({
    queryKey: ['imports', projectId],
    queryFn: ({ signal }) => client.request(base, { schema: importListSchema, signal }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['imports', projectId] });
  return (
    <div className="mt-6 flex flex-col gap-6">
      {canImport ? (
        <UploadCard
          base={base}
          onUploaded={async (imp) => {
            await refresh();
            open(imp.id);
          }}
        />
      ) : readOnly ? (
        <FormAlert tone="locked">O projeto está arquivado: não recebe importações.</FormAlert>
      ) : null}
      {openId ? (
        <ImportFlow
          key={openId}
          organizationId={organizationId}
          base={base}
          projectId={projectId}
          importId={openId}
          canImport={canImport}
          readOnly={readOnly}
          canRecord={canRecord}
          userId={userId}
          onClose={() => open(null)}
          onChanged={refresh}
        />
      ) : null}
      <section aria-labelledby="imports-history" className="flex flex-col gap-3">
        <h2 id="imports-history" className="text-lg font-bold">
          Histórico
        </h2>
        {list.isPending ? (
          <LoadingState rows={3} label="Carregando as importações" />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : !list.data.items.length ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-10"
            icon={FileSpreadsheet}
            title="Nenhuma importação ainda"
            description="As planilhas enviadas aparecem aqui, com a situação e o resumo do processamento."
          />
        ) : (
          <ul aria-label="Importações do projeto" className="flex flex-col gap-2">
            {list.data.items.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  onClick={() => open(i.id)}
                  className="flex min-h-11 w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-4 py-3 text-left text-sm hover:bg-muted/50"
                  aria-current={openId === i.id ? 'true' : undefined}
                >
                  <span className="font-medium">{i.fileName}</span>
                  <span className="text-muted-foreground">
                    {IMPORT_STATUS_LABELS[i.status]}
                    {i.status === 'DONE' ? ` · ${i.accepted} aceitas, ${i.rejected} recusadas` : ''} ·{' '}
                    {formatDateTime(i.createdAt)} · {i.createdByName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function UploadCard({ base, onUploaded }: { base: string; onUploaded: (imp: Import) => Promise<void> }) {
  const { client } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    meta: { handlesError: true },
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return client.request(base, { method: 'POST', body: form, schema: importSchema });
    },
    onSuccess: async (imp) => {
      setError(null);
      if (input.current) input.current.value = '';
      toast.success('Planilha lida', { description: `${imp.fileName}: ${imp.rowCount} linhas` });
      await onUploaded(imp);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <section aria-labelledby={`${id}-title`} className="rounded-lg border border-border p-4">
      <h2 id={`${id}-title`} className="text-lg font-bold">
        Nova importação
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Planilha .xlsx (a primeira aba) ou .csv, até 10 MB e 5.000 linhas, com os títulos na primeira linha.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label htmlFor={`${id}-file`} className="sr-only">
          Planilha para importar
        </label>
        <input
          ref={input}
          id={`${id}-file`}
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          disabled={upload.isPending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const ok = /\.(xlsx|csv)$/i.test(file.name);
            if (!ok) {
              setError('Escolha uma planilha .xlsx ou um arquivo .csv.');
              return;
            }
            if (file.size > 10 * 1024 * 1024) {
              setError('A planilha pode ter até 10 MB.');
              return;
            }
            upload.mutate(file);
          }}
          className="min-h-11 w-full max-w-full min-w-0 text-sm file:mr-3 file:min-h-11 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:text-sm"
        />
        {upload.isPending ? (
          <span className="text-sm text-muted-foreground" role="status">
            <Upload className="mr-1 inline size-4" aria-hidden />
            Lendo a planilha…
          </span>
        ) : null}
      </div>
      {error ? (
        <div className="mt-3">
          <FormAlert tone="error">{error}</FormAlert>
        </div>
      ) : null}
    </section>
  );
}

/** Uma importação aberta: mapear e validar (rascunho), confirmar (validada), acompanhar e baixar o log. */
function ImportFlow({
  organizationId,
  base,
  projectId,
  importId,
  canImport,
  readOnly,
  canRecord,
  userId,
  onClose,
  onChanged,
}: {
  organizationId: string;
  base: string;
  projectId: string;
  importId: string;
  canImport: boolean;
  /** Projeto arquivado: não recebe importações. */
  readOnly: boolean;
  canRecord: boolean;
  userId: string;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['imports', projectId, importId],
    queryFn: ({ signal }) => client.request(`${base}/${importId}`, { schema: importSchema, signal }),
    // Na fila ou processando (e sem ter parado): acompanha até terminar.
    refetchInterval: (q) =>
      q.state.data &&
      !q.state.data.stalled &&
      (q.state.data.status === 'QUEUED' || q.state.data.status === 'PROCESSING')
        ? 1500
        : false,
  });
  const imp = query.data;
  const setData = (next: Import) => queryClient.setQueryData(['imports', projectId, importId], next);
  // Ao terminar (concluída ou falhou), o histórico mostra a situação nova.
  const status = imp?.status;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => {
    if (status === 'DONE' || status === 'FAILED') void onChangedRef.current();
  }, [status]);
  const mine = imp?.createdById === userId;
  const resume = useMutation({
    meta: { handlesError: true },
    mutationFn: () =>
      client.request(`${base}/${importId}/confirm`, { method: 'POST', body: {}, schema: importSchema }),
    onSuccess: async (next) => {
      toast.success('Processamento retomado', {
        description: 'As linhas que faltavam voltaram para a fila.',
      });
      setData(next);
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível retomar', { description: errorMessage(err) }),
  });
  return (
    <section
      aria-label="Importação aberta"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      {query.isPending ? (
        <LoadingState rows={3} label="Carregando a importação" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : imp ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold">{imp.fileName}</h2>
              <p className="text-sm text-muted-foreground">
                {IMPORT_STATUS_LABELS[imp.status]} · {imp.rowCount} linhas de dados · enviada por{' '}
                {imp.createdByName}
              </p>
            </div>
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
          {imp.status === 'DRAFT' || imp.status === 'VALIDATED' ? (
            <>
              <Sample imp={imp} />
              {canImport ? (
                <MappingStep
                  organizationId={organizationId}
                  base={base}
                  imp={imp}
                  onValidated={async (next) => {
                    setData(next);
                    await onChanged();
                  }}
                />
              ) : null}
              {imp.status === 'VALIDATED' ? (
                <ValidationResult
                  base={base}
                  imp={imp}
                  canImport={canImport}
                  canRecord={canRecord}
                  mine={mine}
                  onConfirmed={async (next) => {
                    setData(next);
                    await onChanged();
                  }}
                />
              ) : null}
            </>
          ) : imp.stalled ? (
            <div className="flex flex-col gap-3">
              <FormAlert tone="error">
                O processamento parou sem terminar (sem andamento há mais de 35 minutos). As linhas já
                gravadas ficam; retomar processa só as que faltam.
              </FormAlert>
              {!mine ? (
                <p className="text-sm text-muted-foreground">
                  Quem enviou a planilha ({imp.createdByName}) pode retomar.
                </p>
              ) : !canRecord ? (
                <p className="text-sm text-muted-foreground">
                  Seu perfil não pode lançar resultados e metas (Metas e Resultados): peça a quem pode lançar
                  para importar a planilha de novo.
                </p>
              ) : readOnly ? (
                <p className="text-sm text-muted-foreground">
                  O projeto está arquivado: não recebe importações.
                </p>
              ) : !canImport ? (
                <p className="text-sm text-muted-foreground">
                  Seu perfil não pode importar planilhas (Importações): peça a quem pode importar para
                  retomar.
                </p>
              ) : (
                <div>
                  <Button loading={resume.isPending} onClick={() => !resume.isPending && resume.mutate()}>
                    Retomar o processamento
                  </Button>
                </div>
              )}
            </div>
          ) : imp.status === 'QUEUED' || imp.status === 'PROCESSING' ? (
            <p role="status" className="text-sm">
              {imp.status === 'QUEUED' ? 'Na fila para processar…' : 'Processando as linhas…'} Esta tela se
              atualiza sozinha.
            </p>
          ) : (
            <Finished base={base} imp={imp} />
          )}
        </>
      ) : null}
    </section>
  );
}

function Sample({ imp }: { imp: Import }) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Colunas e primeiras linhas</h3>
      <div
        role="region"
        aria-label="Amostra da planilha"
        tabIndex={0}
        className="relative overflow-x-auto rounded-md border border-border"
      >
        <table className="w-full border-collapse text-xs">
          <caption className="sr-only">Primeiras linhas da planilha {imp.fileName}</caption>
          <thead className="bg-muted/60 text-muted-foreground">
            <tr>
              {imp.headers.map((h, i) => (
                <th key={i} scope="col" className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                  {h || `Coluna ${i + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {imp.sample.map((row, r) => (
              <tr key={r} className="border-t border-border">
                {imp.headers.map((_, c) => (
                  <td key={c} className="px-2 py-1.5 whitespace-nowrap">
                    {row[c]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MappingStep({
  organizationId,
  base,
  imp,
  onValidated,
}: {
  organizationId: string;
  base: string;
  imp: Import;
  onValidated: (imp: Import) => Promise<void>;
}) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const ids = { template: useId(), name: useId() };
  const fieldIds = Object.fromEntries(IMPORT_FIELDS.map((f) => [f, `${ids.template}-${f}`])) as Record<
    ImportFieldName,
    string
  >;
  const initial = (imp.mapping ?? imp.suggested) as Partial<Record<ImportFieldName, number | null>>;
  const [mapping, setMapping] = useState<Record<ImportFieldName, string>>(
    () =>
      Object.fromEntries(
        IMPORT_FIELDS.map((f) => [
          f,
          initial[f] === null || initial[f] === undefined ? '' : String(initial[f]),
        ]),
      ) as Record<ImportFieldName, string>,
  );
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const templatesUrl = `/organizations/${organizationId}/import-templates`;
  const templates = useQuery({
    queryKey: ['import-templates', organizationId],
    queryFn: ({ signal }) => client.request(templatesUrl, { schema: templateListSchema, signal }),
  });
  const toMapping = () =>
    Object.fromEntries(IMPORT_FIELDS.map((f) => [f, mapping[f] === '' ? null : Number(mapping[f])]));
  const validate = useMutation({
    meta: { handlesError: true },
    mutationFn: async () => {
      const parsed = importMappingSchema.safeParse(toMapping());
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Mapeamento inválido.');
      return client.request(`${base}/${imp.id}/validate`, {
        method: 'POST',
        body: { mapping: parsed.data },
        schema: importSchema,
      });
    },
    onSuccess: async (next) => {
      setError(null);
      await onValidated(next);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: async () => {
      const parsed = importMappingSchema.safeParse(toMapping());
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Mapeamento inválido.');
      const title = (i: number | null) => (i === null ? null : (imp.headers[i] ?? null));
      return client.request(templatesUrl, {
        method: 'POST',
        body: {
          name: templateName.trim(),
          columns: {
            kpi: title(parsed.data.kpi),
            period: title(parsed.data.period),
            unit: title(parsed.data.unit),
            value: title(parsed.data.value),
            target: title(parsed.data.target),
            reason: title(parsed.data.reason),
          },
        },
        schema: importTemplateSchema,
      });
    },
    onSuccess: async (t) => {
      setTemplateName('');
      toast.success('Modelo salvo', { description: t.name });
      await queryClient.invalidateQueries({ queryKey: ['import-templates', organizationId] });
    },
    onError: (err) => toast.error('Não foi possível salvar o modelo', { description: errorMessage(err) }),
  });
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: (t: ImportTemplate) =>
      client.request(`${templatesUrl}/${t.id}`, { method: 'DELETE' }).then(() => t),
    onSuccess: async (t) => {
      toast.success('Modelo excluído', { description: t.name });
      await queryClient.invalidateQueries({ queryKey: ['import-templates', organizationId] });
    },
    onError: (err) => toast.error('Não foi possível excluir o modelo', { description: errorMessage(err) }),
  });
  const [chosen, setChosen] = useState('');
  const [deleting, setDeleting] = useState<ImportTemplate | null>(null);
  function applyTemplate(id: string) {
    setChosen(id);
    const t = templates.data?.items.find((x) => x.id === id);
    if (!t) return;
    const { mapping: m, missing } = mappingFromTemplate(t.columns, imp.headers);
    if (!m) {
      setWarning(
        `O modelo "${t.name}" não serve para esta planilha: faltam as colunas ${missing.join(', ')}.`,
      );
      return;
    }
    setMapping(
      Object.fromEntries(IMPORT_FIELDS.map((f) => [f, m[f] === null ? '' : String(m[f])])) as Record<
        ImportFieldName,
        string
      >,
    );
    setWarning(
      missing.length
        ? `A planilha não tem as colunas ${missing.join(', ')} do modelo: ficaram de fora.`
        : null,
    );
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!validate.isPending) validate.mutate();
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">Mapeamento das colunas</h3>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Excluir o modelo?"
        description={`O modelo "${deleting?.name ?? ''}" deixa de estar disponível para toda a empresa. As importações já feitas não mudam.`}
        confirmLabel="Excluir modelo"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          if (!deleting) return;
          const t = deleting;
          setDeleting(null);
          setChosen('');
          remove.mutate(t);
        }}
      />
      {templates.isPending ? (
        <LoadingState rows={1} label="Carregando os modelos" />
      ) : templates.isError ? (
        <ErrorState
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : templates.data?.items.length ? (
        <div className="flex flex-wrap items-end gap-2">
          <FormField id={ids.template} label="Aplicar um modelo">
            <NativeSelect id={ids.template} value={chosen} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Escolha um modelo…</option>
              {templates.data.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          {chosen && can('importacoes', 'delete') ? (
            <Button
              type="button"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setDeleting(templates.data.items.find((x) => x.id === chosen) ?? null)}
            >
              <Trash2 aria-hidden />
              Excluir modelo
            </Button>
          ) : null}
        </div>
      ) : null}
      {warning ? <FormAlert tone="error">{warning}</FormAlert> : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {IMPORT_FIELDS.map((f) => (
          <FormField key={f} id={fieldIds[f]} label={IMPORT_FIELD_LABELS[f]}>
            <NativeSelect
              id={fieldIds[f]}
              value={mapping[f]}
              onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}
            >
              <option value="">{OPTIONAL.includes(f) ? 'Não importar' : 'Escolha a coluna…'}</option>
              {imp.headers.map((h, i) => (
                <option key={i} value={String(i)}>
                  {h || `Coluna ${i + 1}`}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        ))}
      </div>
      {error ? <FormAlert tone="error">{error}</FormAlert> : null}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <FormField id={ids.name} label="Salvar este mapeamento como modelo">
            <Input
              id={ids.name}
              value={templateName}
              maxLength={80}
              placeholder="Ex.: Planilha mensal da controladoria"
              onChange={(e) => setTemplateName(e.target.value)}
              className="h-11"
            />
          </FormField>
          <Button
            type="button"
            variant="outline"
            disabled={templateName.trim().length < 2 || save.isPending}
            onClick={() => save.mutate()}
          >
            <Save aria-hidden />
            Salvar modelo
          </Button>
        </div>
        <Button type="submit" loading={validate.isPending}>
          Validar as linhas
        </Button>
      </div>
    </form>
  );
}

function ValidationResult({
  base,
  imp,
  canImport,
  canRecord,
  mine,
  onConfirmed,
}: {
  base: string;
  imp: Import;
  canImport: boolean;
  canRecord: boolean;
  mine: boolean;
  onConfirmed: (imp: Import) => Promise<void>;
}) {
  const { client } = useAuth();
  const id = useId();
  const [reason, setReason] = useState('');
  const confirm = useMutation({
    meta: { handlesError: true },
    mutationFn: () =>
      client.request(`${base}/${imp.id}/confirm`, {
        method: 'POST',
        body: reason.trim() ? { reason: reason.trim() } : {},
        schema: importSchema,
      }),
    onSuccess: async (next) => {
      toast.success('Importação confirmada', { description: 'As linhas válidas estão sendo processadas.' });
      await onConfirmed(next);
    },
    onError: (err) => toast.error('Não foi possível confirmar', { description: errorMessage(err) }),
  });
  const rejected = imp.rowCount - (imp.validRows ?? 0);
  // A API guarda as primeiras inconsistências (até 500): com mais linhas recusadas, a lista foi cortada.
  const truncated = imp.issues.length > 0 && rejected > new Set(imp.issues.map((i) => i.line)).size;
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">Validação</h3>
      <p role="status" className="text-sm">
        {imp.validRows} de {imp.rowCount} linhas válidas
        {rejected ? ` · ${rejected} com inconsistências (não serão importadas)` : ''}.
      </p>
      {truncated ? (
        <p className="text-xs text-muted-foreground">
          Mostrando as primeiras {imp.issues.length} inconsistências. Depois de importar, o log traz todas as
          linhas recusadas com o motivo.
        </p>
      ) : null}
      {imp.issues.length ? (
        <div
          role="region"
          aria-label="Inconsistências por linha"
          tabIndex={0}
          className="relative max-h-80 overflow-auto rounded-md border border-border"
        >
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Inconsistências encontradas, por linha da planilha</caption>
            <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Linha
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Campo
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Problema
                </th>
              </tr>
            </thead>
            <tbody>
              {imp.issues.map((i, n) => (
                <tr key={n} className="border-t border-border">
                  <td className="px-3 py-2 tabular">{i.line}</td>
                  <td className="px-3 py-2">{i.field ? IMPORT_FIELD_LABELS[i.field] : '—'}</td>
                  <td className="px-3 py-2">{i.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <FormAlert tone="success">Nenhuma inconsistência encontrada.</FormAlert>
      )}
      {canImport && imp.validRows && !canRecord ? (
        <FormAlert tone="locked">
          Seu perfil não pode lançar resultados e metas (Metas e Resultados): a importação grava os dois. Peça
          a quem pode lançar para importar a planilha.
        </FormAlert>
      ) : canImport && imp.validRows && !mine ? (
        <FormAlert tone="locked">
          Só quem enviou a planilha ({imp.createdByName}) confirma a importação.
        </FormAlert>
      ) : null}
      {canImport && canRecord && mine && imp.validRows ? (
        <div className="flex flex-col gap-2">
          <FormField id={`${id}-reason`} label="Justificativa para correções (opcional)">
            <textarea
              id={`${id}-reason`}
              rows={2}
              maxLength={1000}
              value={reason}
              placeholder="Vale para as linhas que corrigem valor já lançado ou de mês fechado, quando a planilha não traz a justificativa."
              onChange={(e) => setReason(e.target.value)}
              className={textareaClass}
            />
          </FormField>
          <div className="flex justify-end">
            <Button loading={confirm.isPending} onClick={() => !confirm.isPending && confirm.mutate()}>
              Confirmar e importar {imp.validRows} {imp.validRows === 1 ? 'linha' : 'linhas'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Finished({ base, imp }: { base: string; imp: Import }) {
  const { client } = useAuth();
  const log = useMutation({
    meta: { handlesError: true },
    mutationFn: async () =>
      saveBlobFile({
        fileName: `log-importacao-${imp.id.slice(0, 8)}.csv`,
        title: `Log da importação — ${imp.fileName}`,
        blob: await client.download(`${base}/${imp.id}/log.csv`),
      }),
    onError: (err) => toast.error('Não foi possível baixar o log', { description: errorMessage(err) }),
  });
  return (
    <div className="flex flex-col gap-3">
      {imp.status === 'FAILED' ? (
        <FormAlert tone="error">
          A importação falhou: {imp.error ?? 'erro ao processar a planilha.'} O log mostra as linhas
          processadas até a falha.
        </FormAlert>
      ) : null}
      <dl aria-label="Resumo do processamento" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs text-muted-foreground">Aceitas</dt>
          <dd className="text-lg font-bold tabular">{imp.accepted ?? 0}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs text-muted-foreground">Recusadas</dt>
          <dd className="text-lg font-bold tabular">{imp.rejected ?? 0}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs text-muted-foreground">
            {imp.status === 'FAILED' ? 'Encerrada em' : 'Concluída em'}
          </dt>
          <dd className="text-sm font-medium">{imp.finishedAt ? formatDateTime(imp.finishedAt) : '—'}</dd>
        </div>
      </dl>
      {imp.status === 'DONE' ? (
        <p className="text-sm text-muted-foreground">
          Os resultados e metas aceitos já estão nos indicadores, com a origem "importado". O log traz cada
          linha, aceita ou recusada, com o motivo.
        </p>
      ) : null}
      <div>
        <Button variant="outline" loading={log.isPending} onClick={() => !log.isPending && log.mutate()}>
          <Download aria-hidden />
          Baixar o log (CSV)
        </Button>
      </div>
    </div>
  );
}
